import React from 'react';
import Select from 'react-select';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';

export class JobPositionDropdown extends React.Component {
	state = {
		loaded: false,
		inputValue: '',
		options: [],
		page: 1,
		hasMore: false,
		isLoading: false
	};

	MIN_SEARCH_CHARS = 3;
	PAGE_SIZE = 20;
	MAX_OPTIONS = 100;

	async componentDidMount() {
		this.setState({ loaded: true });
	}

	render() {
		if (this.state.loaded) {
			const { placeholder = 'Job Position' } = this.props;

			return (
				<Select
					menuPosition="fixed"
					className={
						'react-select ' +
						(this.props.styleVersion ? this.props.styleVersion : '') +
						(this.props.isInvalid ? ' has-error' : '')
					}
					classNamePrefix={
						this.props.classNamePrefix ||
						'react-select' +
							(this.props.styleVersion ? `-${this.props.styleVersion}` : '')
					}
					value={this.props.value}
					placeholder={placeholder}
					options={this.state.options}
					isClearable
					isDisabled={this.props.disabled}
					isLoading={this.state.isLoading}
					components={{
						IndicatorSeparator: null,
						DropdownIndicator: () => (
							<img src="/ats/icons/chevron-down-icon.svg" alt="" />
						)
					}}
					styles={{
						container: (base, state) => ({
							...base,
							...(state.isDisabled && {
								opacity: 0.5,
								cursor: 'not-allowed'
							})
						}),
						control: base => ({
							...base,
							paddingRight: 12,
							...(this.props.customStyles?.control || {})
						}),
						option: (base, state) => ({
							...base,
							...(state.isSelected && {
								backgroundColor: '#b9c3ff',
								color: '#717680'
							})
						})
					}}
					inputValue={this.state.inputValue}
					onInputChange={this.handleInputChange}
					getOptionValue={this.getOptionValue}
					getOptionLabel={this.getOptionLabel}
					noOptionsMessage={this.noOptionsMessage}
					onMenuScrollToBottom={this.handleMenuScrollToBottom}
					onChange={item => {
						// Clear controlled inputValue so selected value is shown.
						this.setState({ inputValue: '' });
						if (this.props.onValueChange) {
							this.props.onValueChange(item);
						}
					}}
					{...this.props.AsyncProps}
				/>
			);
		} else {
			return null;
		}
	}

	handleInputChange = (newValue, { action }) => {
		// react-select triggers different actions; we only care about actual typing.
		if (action !== 'input-change') return newValue;

		const inputValue = (newValue || '').trimStart();
		this.setState(
			{
				inputValue,
				page: 1,
				hasMore: false,
				options: []
			},
			() => {
				this.debouncedSearch(inputValue);
			}
		);

		return inputValue;
	};

	debouncedSearchTimer = null;

	debouncedSearch = inputValue => {
		if (this.debouncedSearchTimer) {
			clearTimeout(this.debouncedSearchTimer);
		}

		this.debouncedSearchTimer = setTimeout(() => {
			this.searchFirstPage(inputValue);
		}, 250);
	};

	searchFirstPage = async inputValue => {
		const q = (inputValue || '').trim();
		if (q.length < this.MIN_SEARCH_CHARS) {
			this.setState({ options: [], hasMore: false, isLoading: false, page: 1 });
			return;
		}

		this.setState({ isLoading: true });
		try {
			const rows = await this.getJobs({
				title: q,
				pagination: { page: 1, limit: this.PAGE_SIZE }
			});

			// Keep currently selected value visible if it isn't in results.
			const selected = this.props.value ? [this.props.value] : [];
			const byId = new Map();
			[...selected, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			this.setState({
				options: Array.from(byId.values()).slice(0, this.MAX_OPTIONS),
				page: 1,
				hasMore: rows.length === this.PAGE_SIZE,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false, options: [], hasMore: false, page: 1 });
		}
	};

	handleMenuScrollToBottom = async () => {
		const q = (this.state.inputValue || '').trim();
		if (this.state.isLoading) return;
		if (!this.state.hasMore) return;
		if (q.length < this.MIN_SEARCH_CHARS) return;

		const nextPage = this.state.page + 1;
		this.setState({ isLoading: true });
		try {
			const rows = await this.getJobs({
				title: q,
				pagination: { page: nextPage, limit: this.PAGE_SIZE }
			});

			const byId = new Map();
			[...this.state.options, ...rows].forEach(r => {
				if (r && r.id != null) byId.set(r.id, r);
			});

			const nextOptions = Array.from(byId.values()).slice(0, this.MAX_OPTIONS);

			this.setState({
				options: nextOptions,
				page: nextPage,
				hasMore:
					rows.length === this.PAGE_SIZE &&
					nextOptions.length < this.MAX_OPTIONS,
				isLoading: false
			});
		} catch (e) {
			this.setState({ isLoading: false });
		}
	};

	getJobs = async inputValue => {
		const result = await jobPositionStore.getJobs({
			...inputValue,
			applicantId: this.props.filterByApplicantId,
			...(this.props.showOnlyOpenJobs ? { status: ['open'] } : {})
		});
		return result.data.rows;
	};

	noOptionsMessage = value => {
		if (
			value.inputValue &&
			value.inputValue.trim().length >= this.MIN_SEARCH_CHARS
		) {
			return 'No matches found';
		} else {
			return `Type at least ${this.MIN_SEARCH_CHARS} characters to search job positions`;
		}
	};

	getOptionValue = option => option.id;

	getOptionLabel = option => {
		const clientName = option.client?.name?.trim();
		const title = option.title?.trim();
		let label = `#${option.id}`;
		if (clientName && title) {
			// If the client name appears in the title (case-insensitive), do not include client.name in the label
			const regex = new RegExp(
				`\\b${clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
				'i'
			);
			if (regex.test(title)) {
				label = `#${option.id} - ${title}`;
			} else {
				label = `#${option.id} - ${clientName} - ${title}`;
			}
		} else if (title) {
			label = `#${option.id} - ${title}`;
		}

		if (option.status && option.status !== 'open') {
			const status = jobPositionStore.getStatusByValue(option.status);
			if (status?.text) {
				return `${label} (${status.text})`;
			}
		}

		return label;
	};
}
